import { useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent, PointerEvent as ReactPointerEvent } from 'react';
import type { QuickSwitchRailItem } from '../hooks/useSessionQuickSwitch';
import { sessionDisplayMonogram } from '../utils/sessionDisplay';
import styles from './SessionQuickSwitchRail.module.css';

const LONG_PRESS_REORDER_MS = 360;
const DRAG_CANCEL_DISTANCE_PX = 8;

interface SessionQuickSwitchRailProps {
  items: QuickSwitchRailItem[];
  side: 'left' | 'right';
  onSessionSelect: (sessionKey: string) => void;
  onItemsReorder: (orderedSessionKeys: string[]) => void;
}

interface PointerGesture {
  pointerId: number;
  sessionKey: string;
  startX: number;
  startY: number;
  capturedElement: HTMLButtonElement | null;
  longPressTimer: ReturnType<typeof window.setTimeout> | null;
  reorderActive: boolean;
}

type PointerLikeEvent = ReactPointerEvent<HTMLButtonElement> | PointerEvent;

function railItemTitle(item: QuickSwitchRailItem): string {
  const bits = [item.label];

  if (item.isActive) {
    bits.push('Active');
  } else if (item.needsAttention) {
    bits.push('Needs attention');
  } else {
    bits.push('Idle');
  }

  if (item.isPinned) {
    bits.push('Pinned');
  }

  return bits.join(' — ');
}

function railItemStateLabel(item: QuickSwitchRailItem): string {
  if (item.isActive) return 'Active';
  if (item.needsAttention) return 'Attention';
  return 'Idle';
}

function moveItem(items: QuickSwitchRailItem[], draggedSessionKey: string | null, targetSessionKey: string | null) {
  if (!draggedSessionKey || !targetSessionKey || draggedSessionKey === targetSessionKey) return items;

  const next = [...items];
  const from = next.findIndex((item) => item.sessionKey === draggedSessionKey);
  const to = next.findIndex((item) => item.sessionKey === targetSessionKey);
  if (from < 0 || to < 0) return items;

  const [dragged] = next.splice(from, 1);
  next.splice(to, 0, dragged);
  return next;
}

function sessionKeyFromRailPosition(
  rail: HTMLElement | null,
  clientY: number,
  ignoredSessionKey?: string | null,
): string | null {
  if (!rail) return null;

  const tabElements = Array.from(
    rail.querySelectorAll<HTMLElement>('[data-quick-switch-session-key]'),
  );
  if (tabElements.length === 0) return null;

  let closestSessionKey: string | null = null;
  let closestDistance = Number.POSITIVE_INFINITY;

  for (const element of tabElements) {
    const sessionKey = element.dataset.quickSwitchSessionKey ?? null;
    if (!sessionKey || sessionKey === ignoredSessionKey) continue;

    const rect = element.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) continue;
    const midpoint = rect.top + rect.height / 2;
    const distance = Math.abs(clientY - midpoint);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestSessionKey = sessionKey;
    }
  }

  return closestSessionKey;
}

function sessionKeyFromPoint(
  clientX: number,
  clientY: number,
  rail: HTMLElement | null,
  ignoredSessionKey?: string | null,
): string | null {
  if (typeof document === 'undefined' || typeof document.elementsFromPoint !== 'function') {
    return sessionKeyFromRailPosition(rail, clientY, ignoredSessionKey);
  }

  for (const element of document.elementsFromPoint(clientX, clientY)) {
    const keyedElement = element.closest<HTMLElement>('[data-quick-switch-session-key]');
    const sessionKey = keyedElement?.dataset.quickSwitchSessionKey;
    if (sessionKey && sessionKey !== ignoredSessionKey) return sessionKey;
  }

  return sessionKeyFromRailPosition(rail, clientY, ignoredSessionKey);
}

export function SessionQuickSwitchRail({
  items,
  side,
  onSessionSelect,
  onItemsReorder,
}: SessionQuickSwitchRailProps) {
  const railRef = useRef<HTMLElement | null>(null);
  const gestureRef = useRef<PointerGesture | null>(null);
  const dropTargetSessionKeyRef = useRef<string | null>(null);
  const suppressNextClickRef = useRef(false);
  const [draggingSessionKey, setDraggingSessionKey] = useState<string | null>(null);
  const [dropTargetSessionKey, setDropTargetSessionKey] = useState<string | null>(null);

  const displayItems = useMemo(
    () => moveItem(items, draggingSessionKey, dropTargetSessionKey),
    [items, draggingSessionKey, dropTargetSessionKey],
  );

  const clearLongPressTimer = () => {
    const gesture = gestureRef.current;
    if (gesture?.longPressTimer) {
      window.clearTimeout(gesture.longPressTimer);
      gesture.longPressTimer = null;
    }
  };

  const resetDragState = () => {
    dropTargetSessionKeyRef.current = null;
    setDraggingSessionKey(null);
    setDropTargetSessionKey(null);
  };

  const suppressClickOnce = () => {
    suppressNextClickRef.current = true;
    window.setTimeout(() => {
      suppressNextClickRef.current = false;
    }, 0);
  };

  const updateDropTargetFromPointer = (event: PointerLikeEvent, fallbackSessionKey?: string) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;

    if (!gesture.reorderActive) {
      const deltaX = event.clientX - gesture.startX;
      const deltaY = event.clientY - gesture.startY;
      if (Math.hypot(deltaX, deltaY) > DRAG_CANCEL_DISTANCE_PX) {
        clearLongPressTimer();
      }
      return;
    }

    event.preventDefault();
    const nextDropTargetSessionKey = sessionKeyFromPoint(event.clientX, event.clientY, railRef.current, gesture.sessionKey)
      ?? fallbackSessionKey
      ?? dropTargetSessionKeyRef.current
      ?? gesture.sessionKey;
    dropTargetSessionKeyRef.current = nextDropTargetSessionKey;
    setDropTargetSessionKey(nextDropTargetSessionKey);
  };

  const finishPointerGesture = (event: PointerLikeEvent) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;

    clearLongPressTimer();
    gestureRef.current = null;
    gesture.capturedElement?.releasePointerCapture?.(event.pointerId);

    if (!gesture.reorderActive) return;

    event.preventDefault();
    event.stopPropagation();
    suppressClickOnce();

    const finalItems = moveItem(items, gesture.sessionKey, dropTargetSessionKeyRef.current);
    const nextOrder = finalItems.map((item) => item.sessionKey);
    if (nextOrder.some((sessionKey, index) => sessionKey !== items[index]?.sessionKey)) {
      onItemsReorder(nextOrder);
    }

    resetDragState();
  };

  const cancelPointerGesture = (event: PointerLikeEvent) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;

    clearLongPressTimer();
    gestureRef.current = null;
    gesture.capturedElement?.releasePointerCapture?.(event.pointerId);
    resetDragState();
  };

  const handleLostPointerCapture = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;

    if (gesture.reorderActive) {
      // Chromium can drop pointer capture during touch dragging once the finger
      // moves away from the narrow pill. Do not treat that as a failed drag;
      // document-level pointer listeners below will keep tracking and finish it.
      return;
    }

    cancelPointerGesture(event);
  };

  useEffect(() => {
    const handleDocumentPointerMove = (event: PointerEvent) => {
      updateDropTargetFromPointer(event);
    };
    const handleDocumentPointerUp = (event: PointerEvent) => {
      finishPointerGesture(event);
    };
    const handleDocumentPointerCancel = (event: PointerEvent) => {
      cancelPointerGesture(event);
    };

    document.addEventListener('pointermove', handleDocumentPointerMove);
    document.addEventListener('pointerup', handleDocumentPointerUp);
    document.addEventListener('pointercancel', handleDocumentPointerCancel);
    return () => {
      document.removeEventListener('pointermove', handleDocumentPointerMove);
      document.removeEventListener('pointerup', handleDocumentPointerUp);
      document.removeEventListener('pointercancel', handleDocumentPointerCancel);
    };
  });

  const handlePointerDown = (event: ReactPointerEvent<HTMLButtonElement>, sessionKey: string) => {
    if (!event.isPrimary || event.button !== 0 || items.length < 2) return;

    clearLongPressTimer();
    resetDragState();

    const gesture: PointerGesture = {
      pointerId: event.pointerId,
      sessionKey,
      startX: event.clientX,
      startY: event.clientY,
      capturedElement: event.currentTarget,
      longPressTimer: null,
      reorderActive: false,
    };

    gesture.longPressTimer = window.setTimeout(() => {
      gesture.reorderActive = true;
      dropTargetSessionKeyRef.current = sessionKey;
      setDraggingSessionKey(sessionKey);
      setDropTargetSessionKey(sessionKey);
    }, LONG_PRESS_REORDER_MS);

    gestureRef.current = gesture;
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleClick = (event: MouseEvent<HTMLButtonElement>, sessionKey: string) => {
    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    onSessionSelect(sessionKey);
  };

  if (items.length === 0) return null;

  return (
    <nav
      ref={railRef}
      className={[
        styles.rail,
        side === 'left' ? styles.railLeft : styles.railRight,
        draggingSessionKey ? styles.railDragging : '',
      ].filter(Boolean).join(' ')}
      aria-label={`Quick switch sessions (${side} side)`}
    >
      {displayItems.map((item) => {
        const isDragging = item.sessionKey === draggingSessionKey;
        const isDropTarget = draggingSessionKey != null && item.sessionKey === dropTargetSessionKey;

        return (
          <button
            key={item.sessionKey}
            type="button"
            className={[
              styles.tab,
              side === 'left' ? styles.tabLeft : styles.tabRight,
              item.isActive ? styles.tabActive : styles.tabIdle,
              item.needsAttention ? styles.tabAttention : '',
              isDragging ? styles.tabDragging : '',
              isDropTarget ? styles.tabDropTarget : '',
            ].filter(Boolean).join(' ')}
            data-quick-switch-session-key={item.sessionKey}
            onPointerDown={(event) => handlePointerDown(event, item.sessionKey)}
            onPointerMove={(event) => updateDropTargetFromPointer(event, item.sessionKey)}
            onPointerUp={finishPointerGesture}
            onPointerCancel={cancelPointerGesture}
            onLostPointerCapture={handleLostPointerCapture}
            onClick={(event) => handleClick(event, item.sessionKey)}
            onContextMenu={(event) => {
              if (draggingSessionKey || suppressNextClickRef.current) event.preventDefault();
            }}
            aria-label={`Switch to ${item.label}${item.needsAttention ? ' (needs attention)' : ''}`}
            aria-current={item.isActive ? 'page' : undefined}
            aria-grabbed={isDragging ? true : undefined}
            title={railItemTitle(item)}
          >
            <span className={styles.identity} aria-hidden="true">
              {sessionDisplayMonogram(item.label)}
            </span>
            <span className={styles.copy}>
              <span className={styles.label}>{item.label}</span>
              <span className={styles.meta}>
                <span className={styles.stateDot} aria-hidden="true" />
                <span>{isDragging ? 'Moving' : railItemStateLabel(item)}</span>
              </span>
            </span>
            {item.isPinned ? <span className={styles.pinMark} aria-hidden="true">★</span> : null}
          </button>
        );
      })}
    </nav>
  );
}
